import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CalendarPlusIcon } from 'lucide-react'

import { ApiError } from '@/api/errors'
import { interactionsApi } from '@/api/interactions'
import { queryKeys } from '@/api/keys'
import { useWeekInteractions } from '@/components/calendar/use-week-interactions'
import { WeekCalendar } from '@/components/calendar/week-calendar'
import { EmptyState } from '@/components/empty-state'
import { Frame } from '@/components/frame'
import { InteractionsTable } from '@/components/interactions/interactions-table'
import { useInteractionEditor } from '@/components/interactions/use-interaction-editor'
import { PageHeader } from '@/components/page-header'
import { Pagination } from '@/components/pagination'
import { SegmentedControl } from '@/components/segmented-control'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { addDays, zonedInstant } from '@/lib/calendar'
import { integer, matching, oneOf, readSearch } from '@/lib/search-params'

const SHAPE = {
  /** Un jour de la semaine affichée ; la semaine en cours à défaut. */
  semaine: matching(/^\d{4}-\d{2}-\d{2}$/),
  /** `equipe` : tous les échanges ; les miens à défaut. */
  qui: oneOf(['equipe'] as const),
  /** `liste` : le tableau des échanges, du plus récent au plus ancien ; la semaine à défaut. */
  vue: oneOf(['liste'] as const),
  page: integer({ min: 1 }),
}

export const Route = createFileRoute('/_app/echanges')({
  validateSearch: (search: Record<string, unknown>) => readSearch(SHAPE, search),
  component: EchangesPage,
})

/**
 * Le calendrier des échanges (lot 10) : la semaine, d'abord la mienne — là où
 * je participe —, ou celle de toute l'équipe. Planifier depuis un créneau.
 */
function EchangesPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const mine = search.qui !== 'equipe'
  const { query, week, today, timeZone } = useWeekInteractions({ day: search.semaine, mine })
  const editor = useInteractionEditor()

  const goTo = (day: string | undefined) => {
    void navigate({ search: (current) => ({ ...current, semaine: day }) })
  }

  const plan = (day: string, hour = 9, minute = 0) => {
    editor.openCreate({
      status: 'planned',
      occurredAt: zonedInstant(day, timeZone, hour, minute),
      durationMinutes: 30,
    })
  }

  // Au-delà de 200 échanges, le serveur refuse plutôt que de tronquer la semaine.
  const tooMany = query.error instanceof ApiError && query.error.status === 422 && !mine

  // La liste : le même tableau que l'onglet d'un client, avec la colonne Client.
  const listView = search.vue === 'liste'
  const listParams = {
    ...(mine ? { participant_id: 'me' as const } : {}),
    sort: '-occurred_at' as const,
    page: search.page ?? 1,
    page_size: 25,
  }
  const list = useQuery({
    queryKey: queryKeys.interactions.list(listParams),
    queryFn: () => interactionsApi.list(listParams),
    enabled: listView,
    placeholderData: (previous) => previous,
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Échanges"
        documentTitle="Échanges"
        actions={
          <Button
            onClick={() => {
              plan(week.days.includes(today) ? today : week.monday)
            }}
          >
            <CalendarPlusIcon />
            Planifier un échange
          </Button>
        }
      />

      <Frame>
        <div className="flex flex-wrap items-center gap-3">
          <SegmentedControl
            label="Affichage des échanges"
            value={listView ? 'liste' : 'semaine'}
            options={[
              { value: 'semaine', label: 'Semaine' },
              { value: 'liste', label: 'Liste' },
            ]}
            onChange={(value) => {
              void navigate({
                search: (current) => ({
                  ...current,
                  vue: value === 'liste' ? 'liste' : undefined,
                  page: undefined,
                }),
              })
            }}
          />
          <SegmentedControl
            label="Échanges affichés"
            value={mine ? 'miens' : 'equipe'}
            options={[
              { value: 'miens', label: 'Les miens' },
              { value: 'equipe', label: 'Toute l’équipe' },
            ]}
            onChange={(value) => {
              void navigate({
                search: (current) => ({
                  ...current,
                  qui: value === 'equipe' ? 'equipe' : undefined,
                  page: undefined,
                }),
              })
            }}
          />
        </div>

        {listView ? (
          list.isPending ? (
            <Skeleton className="h-96 w-full rounded-lg" />
          ) : list.isError ? (
            <EmptyState
              icon={CalendarPlusIcon}
              title="Impossible de charger les échanges"
              description={list.error instanceof ApiError ? list.error.message : 'Réessayez.'}
            />
          ) : list.data.items.length === 0 ? (
            <EmptyState
              icon={CalendarPlusIcon}
              title={mine ? 'Aucun échange où vous participez' : 'Aucun échange'}
              description="Aucun échange pour l’instant."
            />
          ) : (
            <>
              <InteractionsTable items={list.data.items} showClient />
              {list.data.total > list.data.page_size && (
                <Pagination
                  page={list.data.page}
                  pageSize={list.data.page_size}
                  total={list.data.total}
                  itemLabel="échange"
                  onPageChange={(page) => {
                    void navigate({
                      search: (current) => ({ ...current, page: page === 1 ? undefined : page }),
                    })
                  }}
                />
              )}
            </>
          )
        ) : tooMany ? (
          <EmptyState
            icon={CalendarPlusIcon}
            title="Trop d’échanges cette semaine pour tout afficher"
            description="Affichez les vôtres : la semaine de toute l’équipe dépasse 200 échanges."
            action={
              <Button
                variant="outline"
                onClick={() => {
                  void navigate({ search: (current) => ({ ...current, qui: undefined }) })
                }}
              >
                Les miens
              </Button>
            }
          />
        ) : query.isError ? (
          <EmptyState
            icon={CalendarPlusIcon}
            title="Impossible de charger la semaine"
            description={query.error instanceof ApiError ? query.error.message : 'Réessayez.'}
          />
        ) : query.isPending ? (
          <Skeleton className="h-96 w-full rounded-lg" />
        ) : (
          <WeekCalendar
            days={week.days}
            today={today}
            timeZone={timeZone}
            interactions={query.data.items}
            editor={editor}
            showClient
            onPrevious={() => {
              goTo(addDays(week.monday, -7))
            }}
            onNext={() => {
              goTo(addDays(week.monday, 7))
            }}
            onToday={() => {
              goTo(undefined)
            }}
            onSlot={plan}
          />
        )}

        {editor.dialogs}
      </Frame>
    </div>
  )
}
